/**
 * F&O Jobs Tab
 *
 * Full management view for F&O Jobs. Absorbs the old "Stale Jobs" sub-tab
 * from Org Structure. All data is fetched once and filtered client-side for
 * instant filter changes.
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Briefcase, Search, RefreshCw, Archive, RotateCcw, Trash2,
  MoreHorizontal, Loader2, AlertTriangle, CheckCircle, Link2,
  Settings, Eye, ArrowRight, Filter, X, Download, Layers,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { useConfirm } from '@/hooks/useConfirm';
import { jobFamilySyncService, FoJobListItem, FoJobListStats } from '@/services/api/organizationService';
import roleMasterService from '@/services/api/roleMasterService';
import EditFoJobDialog from './EditFoJobDialog';
import ReassignPositionsToJobDialog from './ReassignPositionsToJobDialog';
import CreateFoPositionsDialog from './CreateFoPositionsDialog';

const PAGE_SIZE = 50;

type StatusFilter = 'all' | 'active' | 'retired' | 'stale';
type LinkFilter = 'all' | 'linked' | 'unlinked';

interface FoJobsTabProps {
  onNavigateToRole?: (roleId: number) => void;
}

const FoJobsTab: React.FC<FoJobsTabProps> = ({ onNavigateToRole }) => {
  const { confirm: showConfirm } = useConfirm();
  const [jobs, setJobs] = useState<FoJobListItem[]>([]);
  const [stats, setStats] = useState<FoJobListStats | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all');
  const [jobFamilyFilter, setJobFamilyFilter] = useState<string>('all');
  const [page, setPage] = useState(0);

  // Selection
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);

  // Dialogs
  const [editJobId, setEditJobId] = useState<string | null>(null);
  const [reassignSourceJob, setReassignSourceJob] = useState<FoJobListItem | null>(null);
  const [createPositionsJob, setCreatePositionsJob] = useState<FoJobListItem | null>(null);

  // ── Load data ───────────────────────────────────────────────────────────

  const loadData = useCallback(async (refresh: boolean = false) => {
    setIsLoading(true);
    try {
      const data = await jobFamilySyncService.listFoJobs(refresh);
      setJobs(data.items);
      setStats(data.stats);
    } catch (e) {
      console.error('Failed to load F&O jobs:', e);
      setSyncMessage('Failed to load F&O jobs');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── Client-side filtering ──────────────────────────────────────────────

  const jobFamilies = useMemo(() => {
    const set = new Set<string>();
    jobs.forEach(j => { if (j.job_family_id) set.add(j.job_family_id); });
    return Array.from(set).sort();
  }, [jobs]);

  const filtered = useMemo(() => {
    let list = jobs;

    if (statusFilter === 'active') {
      list = list.filter(j => !j.is_expired && j.positions_count > 0);
    } else if (statusFilter === 'retired') {
      list = list.filter(j => j.is_expired);
    } else if (statusFilter === 'stale') {
      list = list.filter(j => !j.is_expired && j.positions_count === 0);
    }

    if (linkFilter === 'linked') {
      list = list.filter(j => j.linked_role_id !== null && j.linked_role_id !== undefined);
    } else if (linkFilter === 'unlinked') {
      list = list.filter(j => !j.linked_role_id);
    }

    if (jobFamilyFilter !== 'all') {
      list = list.filter(j => j.job_family_id === jobFamilyFilter);
    }

    if (search.trim()) {
      const s = search.trim().toLowerCase();
      list = list.filter(j =>
        j.job_id.toLowerCase().includes(s) ||
        (j.title_id || '').toLowerCase().includes(s) ||
        (j.description || '').toLowerCase().includes(s) ||
        (j.linked_role_code || '').toLowerCase().includes(s) ||
        (j.linked_role_title || '').toLowerCase().includes(s)
      );
    }

    return list;
  }, [jobs, statusFilter, linkFilter, jobFamilyFilter, search]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
    setSelectedIds(new Set());
  }, [search, statusFilter, linkFilter, jobFamilyFilter]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pagedJobs = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // ── Selection helpers ──────────────────────────────────────────────────

  const toggleSelected = (jobId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };

  const togglePageAll = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const allSelected = pagedJobs.every(j => next.has(j.job_id));
      if (allSelected) {
        pagedJobs.forEach(j => next.delete(j.job_id));
      } else {
        pagedJobs.forEach(j => next.add(j.job_id));
      }
      return next;
    });
  };

  const pageAllSelected = pagedJobs.length > 0 && pagedJobs.every(j => selectedIds.has(j.job_id));

  // ── Derived selection stats ────────────────────────────────────────────

  const selectedJobs = useMemo(
    () => jobs.filter(j => selectedIds.has(j.job_id)),
    [jobs, selectedIds]
  );
  const selectedActive = selectedJobs.filter(j => !j.is_expired).length;
  const selectedRetired = selectedJobs.filter(j => j.is_expired).length;

  // ── Actions ────────────────────────────────────────────────────────────

  const optimisticUpdateExpiration = (jobIds: string[], is_expired: boolean, expiration: string) => {
    setJobs(prev => prev.map(j =>
      jobIds.includes(j.job_id) ? { ...j, is_expired, expiration } : j
    ));
  };

  const handleBulkRetire = async () => {
    const ids = selectedJobs.filter(j => !j.is_expired).map(j => j.job_id);
    if (ids.length === 0) return;
    const confirmed = await showConfirm(
      `Retire ${ids.length} job${ids.length === 1 ? '' : 's'}? This sets the Expiration date to today and deactivates each Title.`,
      'Bulk Retire Jobs',
      { confirmText: `Retire ${ids.length}`, type: 'warning' },
    );
    if (!confirmed) return;
    setBulkRunning(true);
    try {
      const result = await jobFamilySyncService.bulkRetireJobs(ids);
      const successIds = result.results.filter(r => r.success).map(r => r.job_id);
      optimisticUpdateExpiration(successIds, true, new Date().toISOString());
      setSelectedIds(new Set());
      setSyncMessage(`Retired ${result.succeeded}${result.failed > 0 ? `, ${result.failed} failed` : ''}`);
    } catch (e) {
      console.error('Bulk retire failed:', e);
      setSyncMessage('Bulk retire failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const handleBulkRestore = async () => {
    const ids = selectedJobs.filter(j => j.is_expired).map(j => j.job_id);
    if (ids.length === 0) return;
    const confirmed = await showConfirm(
      `Restore ${ids.length} job${ids.length === 1 ? '' : 's'}? This sets each Expiration to far future and reactivates the Titles.`,
      'Bulk Restore Jobs',
      { confirmText: `Restore ${ids.length}`, type: 'info' },
    );
    if (!confirmed) return;
    setBulkRunning(true);
    try {
      const result = await jobFamilySyncService.bulkUnretireJobs(ids);
      const successIds = result.results.filter(r => r.success).map(r => r.job_id);
      optimisticUpdateExpiration(successIds, false, '2154-12-31T23:59:59Z');
      setSelectedIds(new Set());
      setSyncMessage(`Restored ${result.succeeded}${result.failed > 0 ? `, ${result.failed} failed` : ''}`);
    } catch (e) {
      console.error('Bulk restore failed:', e);
      setSyncMessage('Bulk restore failed');
    } finally {
      setBulkRunning(false);
    }
  };

  const handleRetireOne = async (job: FoJobListItem) => {
    const confirmed = await showConfirm(
      `Retire Job "${job.job_id}"? This sets the Expiration date to today and deactivates the Title.`,
      'Retire Job',
      { confirmText: 'Retire', type: 'warning' }
    );
    if (!confirmed) return;
    try {
      const result: any = await jobFamilySyncService.retireJob(job.job_id);
      if (result.job_expired) {
        optimisticUpdateExpiration([job.job_id], true, result.expiration || new Date().toISOString());
        if (result.partial) {
          setSyncMessage(`Retired "${job.job_id}" — but Title deactivation failed: ${result.title_error || 'unknown'}`);
        } else {
          setSyncMessage(`Retired "${job.job_id}"`);
        }
      } else {
        setSyncMessage(`Retire partial: Job NOT expired (${result.job_error || 'unknown'}). Title: ${result.title_deactivated ? 'deactivated' : 'failed'}.`);
      }
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Retire failed';
      setSyncMessage(`Retire failed: ${detail}`);
      console.error('Retire failed:', e);
    }
  };

  const handleRestoreOne = async (job: FoJobListItem) => {
    const confirmed = await showConfirm(
      `Restore Job "${job.job_id}"? This sets the Expiration back to far future.`,
      'Restore Job',
      { confirmText: 'Restore', type: 'info' }
    );
    if (!confirmed) return;
    try {
      await jobFamilySyncService.unretireJob(job.job_id);
      optimisticUpdateExpiration([job.job_id], false, '2154-12-31T23:59:59Z');
      setSyncMessage(`Restored "${job.job_id}"`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Restore failed';
      setSyncMessage(`Restore failed: ${detail}`);
      console.error('Restore failed:', e);
    }
  };

  const handleDeleteOne = async (job: FoJobListItem) => {
    const confirmed = await showConfirm(
      `Permanently DELETE Job "${job.job_id}" from F&O?\n\nThis is irreversible. Use this only for corrupted jobs that can't be retired normally.\n\nF&O will refuse if any Positions reference the Job.`,
      'Delete Job (Hard Delete)',
      { confirmText: 'Delete', type: 'danger' }
    );
    if (!confirmed) return;
    try {
      await jobFamilySyncService.deleteJob(job.job_id);
      setJobs(prev => prev.filter(j => j.job_id !== job.job_id));
      setSyncMessage(`Deleted job "${job.job_id}"`);
    } catch (e: any) {
      const detail = e?.response?.data?.detail || 'Failed to delete';
      await showConfirm(detail, 'Cannot Delete', { confirmText: 'OK', type: 'warning' });
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await jobFamilySyncService.exportFoJobs();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `fo_jobs_export_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Jobs export failed', e);
    } finally {
      setExporting(false);
    }
  };

  const handleDerivePositions = async () => {
    const confirmed = await showConfirm(
      'Rebuild all Position→Role links from current Job linkage? Positions whose Job no longer matches any Role will be unlinked.',
      'Derive Positions',
      { confirmText: 'Derive', type: 'info' }
    );
    if (!confirmed) return;
    try {
      const result = await roleMasterService.derivePositions();
      setSyncMessage(
        `Derived: ${result.positions_linked} linked, ${result.positions_unlinked ?? 0} cleared, ${result.positions_already_linked} unchanged`
      );
      // Refresh job data so counts update
      loadData();
    } catch (e) {
      console.error('Derive failed:', e);
      setSyncMessage('Derive positions failed');
    }
  };

  // ── Filter active count ─────────────────────────────────────────────────

  const activeFilters =
    (statusFilter !== 'all' ? 1 : 0) +
    (linkFilter !== 'all' ? 1 : 0) +
    (jobFamilyFilter !== 'all' ? 1 : 0);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">
      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-heritage-blue">{stats.total}</div>
              <div className="text-xs text-gray-500">Total Jobs</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-emerald-600">{stats.active - stats.stale}</div>
              <div className="text-xs text-gray-500">Active</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-gray-500">{stats.retired}</div>
              <div className="text-xs text-gray-500">Retired</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-amber-600">{stats.stale}</div>
              <div className="text-xs text-gray-500">Stale (0 pos)</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-emerald-700">{stats.linked}</div>
              <div className="text-xs text-gray-500">Linked to Role</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-3">
              <div className="text-2xl font-bold text-orange-500">{stats.unlinked}</div>
              <div className="text-xs text-gray-500">Unlinked</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search job ID, title, linked role..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="retired">Retired</SelectItem>
            <SelectItem value="stale">Stale</SelectItem>
          </SelectContent>
        </Select>

        <Select value={linkFilter} onValueChange={(v) => setLinkFilter(v as LinkFilter)}>
          <SelectTrigger className="w-[130px]">
            <SelectValue placeholder="Link" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Links</SelectItem>
            <SelectItem value="linked">Linked</SelectItem>
            <SelectItem value="unlinked">Unlinked</SelectItem>
          </SelectContent>
        </Select>

        <Select value={jobFamilyFilter} onValueChange={setJobFamilyFilter}>
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Job Family" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Families</SelectItem>
            {jobFamilies.map(f => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {activeFilters > 0 && (
          <Button
            variant="ghost" size="sm"
            className="text-xs text-gray-500"
            onClick={() => {
              setStatusFilter('all'); setLinkFilter('all'); setJobFamilyFilter('all');
            }}
          >
            <X className="h-3 w-3 mr-1" /> Clear ({activeFilters})
          </Button>
        )}

        <div className="flex-1" />

        <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting}>
          {exporting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Export
        </Button>
        <Button variant="outline" size="sm" onClick={handleDerivePositions}>
          <ArrowRight className="h-4 w-4 mr-1" /> Derive Positions
        </Button>
        <Button variant="outline" size="sm" onClick={() => loadData(true)} disabled={isLoading}>
          <RefreshCw className={cn('h-4 w-4 mr-1', isLoading && 'animate-spin')} /> Refresh
        </Button>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between gap-3 px-3 py-2 bg-heritage-blue/5 border border-heritage-blue/20 rounded-md">
          <div className="text-sm text-gray-700">
            <strong>{selectedIds.size}</strong> selected
            {selectedActive > 0 && <span className="text-gray-500"> · {selectedActive} active</span>}
            {selectedRetired > 0 && <span className="text-gray-500"> · {selectedRetired} retired</span>}
          </div>
          <div className="flex items-center gap-2">
            {selectedActive > 0 && (
              <Button
                size="sm" variant="outline"
                className="h-8 text-xs text-amber-600 border-amber-200 hover:bg-amber-50"
                onClick={handleBulkRetire}
                disabled={bulkRunning}
              >
                {bulkRunning ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Archive className="h-3 w-3 mr-1" />}
                Retire {selectedActive}
              </Button>
            )}
            {selectedRetired > 0 && (
              <Button
                size="sm" variant="outline"
                className="h-8 text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                onClick={handleBulkRestore}
                disabled={bulkRunning}
              >
                {bulkRunning ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RotateCcw className="h-3 w-3 mr-1" />}
                Restore {selectedRetired}
              </Button>
            )}
            <Button
              size="sm" variant="ghost"
              className="h-8 text-xs text-gray-500"
              onClick={() => setSelectedIds(new Set())}
              disabled={bulkRunning}
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {syncMessage && (
        <div className="text-sm text-gray-600 bg-gray-50 border rounded px-3 py-2 flex items-center justify-between">
          <span>{syncMessage}</span>
          <Button variant="ghost" size="sm" onClick={() => setSyncMessage(null)}>
            <X className="h-3 w-3" />
          </Button>
        </div>
      )}

      {/* Table */}
      {isLoading && jobs.length === 0 ? (
        <div className="py-12 text-center text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin inline mr-2" />Loading F&O Jobs (may take up to 10s on cold cache)...
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-12 text-center text-gray-400 text-sm">
          {jobs.length === 0 ? 'No F&O Jobs found.' : 'No jobs match current filters.'}
        </div>
      ) : (
        <>
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50/80">
                  <TableHead className="w-8">
                    <Checkbox checked={pageAllSelected} onCheckedChange={togglePageAll} />
                  </TableHead>
                  <TableHead className="text-xs font-medium">Job ID</TableHead>
                  <TableHead className="text-xs font-medium">Title</TableHead>
                  <TableHead className="text-xs font-medium">Function</TableHead>
                  <TableHead className="text-xs font-medium w-[70px]">Grade</TableHead>
                  <TableHead className="text-xs font-medium text-center w-[60px]">Pos</TableHead>
                  <TableHead className="text-xs font-medium">Linked Role</TableHead>
                  <TableHead className="text-xs font-medium w-[90px]">Status</TableHead>
                  <TableHead className="w-[40px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagedJobs.map(job => {
                  const isSelected = selectedIds.has(job.job_id);
                  const isStale = !job.is_expired && job.positions_count === 0;
                  return (
                    <TableRow
                      key={job.job_id}
                      className={cn(
                        'text-sm',
                        isSelected && 'bg-heritage-blue/5',
                        job.is_expired && 'bg-gray-50/50',
                        isStale && !isSelected && 'bg-amber-50/30',
                      )}
                    >
                      <TableCell className="w-8">
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelected(job.job_id)} />
                      </TableCell>
                      <TableCell className="font-mono text-xs whitespace-nowrap">
                        {job.job_id}
                      </TableCell>
                      <TableCell className={cn('whitespace-nowrap', job.is_expired && 'line-through text-gray-400')}>
                        {job.title_id || <span className="text-gray-300 italic">—</span>}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 whitespace-nowrap">
                        {job.job_family_id || '—'}
                      </TableCell>
                      <TableCell className="text-xs text-gray-600">
                        {job.compensation_level || '—'}
                      </TableCell>
                      <TableCell className="text-center">
                        <span className={cn(
                          'text-xs font-medium',
                          job.positions_count === 0 ? 'text-gray-300' : 'text-gray-700',
                        )}>
                          {job.positions_count}
                        </span>
                      </TableCell>
                      <TableCell className="text-xs">
                        {job.linked_role_id ? (
                          <button
                            className="text-heritage-blue hover:underline text-left"
                            onClick={() => onNavigateToRole?.(job.linked_role_id!)}
                            title={`${job.linked_role_code}: ${job.linked_role_title}`}
                          >
                            <span className="font-mono text-gray-400">{job.linked_role_code}</span>
                            <span className="ml-1">{job.linked_role_title}</span>
                          </button>
                        ) : (
                          <span className="text-gray-300 italic">Unlinked</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {job.is_expired ? (
                          <Badge className="bg-gray-100 text-gray-500 text-[10px] px-1.5 py-0">Retired</Badge>
                        ) : isStale ? (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px] px-1.5 py-0">Stale</Badge>
                        ) : (
                          <Badge className="bg-emerald-100 text-emerald-700 text-[10px] px-1.5 py-0">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-52">
                            <DropdownMenuItem onClick={() => setEditJobId(job.job_id)}>
                              <Settings className="h-4 w-4 mr-2" /> Edit F&O Job
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setReassignSourceJob(job)}
                              disabled={job.positions_count === 0}
                            >
                              <ArrowRight className="h-4 w-4 mr-2" /> Reassign Positions
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => setCreatePositionsJob(job)}
                              disabled={job.is_expired}
                            >
                              <Layers className="h-4 w-4 mr-2" /> Create Positions
                            </DropdownMenuItem>
                            {job.linked_role_id && (
                              <DropdownMenuItem onClick={() => onNavigateToRole?.(job.linked_role_id!)}>
                                <Link2 className="h-4 w-4 mr-2" /> View Linked Role
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            {job.is_expired ? (
                              <DropdownMenuItem onClick={() => handleRestoreOne(job)} className="text-emerald-600">
                                <RotateCcw className="h-4 w-4 mr-2" /> Restore
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => handleRetireOne(job)} className="text-amber-600">
                                <Archive className="h-4 w-4 mr-2" /> Retire
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDeleteOne(job)} className="text-red-600">
                              <Trash2 className="h-4 w-4 mr-2" /> Delete (Hard)
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-gray-500">
                Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filtered.length)} of {filtered.length}
              </div>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                  Previous
                </Button>
                <span className="text-xs text-gray-500 self-center px-2">
                  Page {page + 1} of {pageCount}
                </span>
                <Button variant="outline" size="sm" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Edit F&O Job dialog */}
      <EditFoJobDialog
        open={!!editJobId}
        onOpenChange={(open) => { if (!open) { setEditJobId(null); loadData(); } }}
        jobId={editJobId || ''}
      />

      {/* Reassign Positions dialog (scoped to source job) */}
      {reassignSourceJob && (
        <ReassignPositionsToJobDialog
          open={!!reassignSourceJob}
          onOpenChange={(open) => { if (!open) setReassignSourceJob(null); }}
          sourceJobId={reassignSourceJob.job_id}
          sourceJobTitle={reassignSourceJob.title_id || reassignSourceJob.job_id}
          onComplete={() => { setReassignSourceJob(null); loadData(true); }}
        />
      )}

      {/* Create Positions dialog — stages a provisioning request */}
      <CreateFoPositionsDialog
        open={!!createPositionsJob}
        onOpenChange={(open) => { if (!open) setCreatePositionsJob(null); }}
        job={createPositionsJob}
        onStaged={() => loadData(true)}
      />
    </div>
  );
};

export default FoJobsTab;
